import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation26({ animation, badge, all_animations }: Props) {
  const [showBadge, setShowBadge] = useState(false)
  const [glitchPhase, setGlitchPhase] = useState(0)

  useEffect(() => {
    // Phases de glitch
    const glitchInterval = setInterval(() => {
      setGlitchPhase(prev => (prev + 1) % 10)
    }, 100)

    setTimeout(() => {
      setShowBadge(true)
      setTimeout(() => clearInterval(glitchInterval), 2000)
    }, 500)

    return () => clearInterval(glitchInterval)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes glitchIn {
          0% {
            clip-path: inset(40% 0 60% 0);
            transform: translate(-20px, 0) skewX(10deg);
          }
          20% {
            clip-path: inset(10% 0 85% 0);
            transform: translate(10px, 0) skewX(-5deg);
          }
          40% {
            clip-path: inset(50% 0 30% 0);
            transform: translate(-5px, 0) skewX(2deg);
          }
          60% {
            clip-path: inset(20% 0 60% 0);
            transform: translate(5px, 0) skewX(-2deg);
          }
          80% {
            clip-path: inset(80% 0 5% 0);
            transform: translate(-2px, 0) skewX(1deg);
          }
          100% {
            clip-path: inset(0 0 0 0);
            transform: translate(0, 0) skewX(0deg);
          }
        }
        @keyframes scanline {
          0% { top: 0; }
          100% { top: 100%; }
        }
        .glitch-in {
          animation: glitchIn 1s steps(1) forwards;
        }
        .scanline {
          animation: scanline 2s linear infinite;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh] relative">
        {/* Scanlines */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
          <div className="scanline absolute left-0 w-full h-1 bg-tessan-cream" />
        </div>

        {/* Badge avec effet glitch */}
        <div className={`relative z-10 ${showBadge ? 'glitch-in' : 'opacity-0'}`}>
          {/* Copies décalées pour l'effet glitch */}
          {glitchPhase < 5 && showBadge && (
            <>
              <div
                className="absolute w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream/30 rounded-2xl flex items-center justify-center"
                style={{
                  transform: `translateX(${glitchPhase * 2}px)`,
                  filter: 'hue-rotate(90deg)'
                }}
              >
                <span className="text-6xl sm:text-8xl opacity-50">{badge.emoji}</span>
              </div>
              <div
                className="absolute w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream/30 rounded-2xl flex items-center justify-center"
                style={{
                  transform: `translateX(${-glitchPhase * 2}px)`,
                  filter: 'hue-rotate(-90deg)'
                }}
              >
                <span className="text-6xl sm:text-8xl opacity-50">{badge.emoji}</span>
              </div>
            </>
          )}

          {/* Badge principal */}
          <div className="relative w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
            <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
          </div>
        </div>

        <div className={`mt-8 text-center transition-all duration-700 delay-1000 ${showBadge ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
